import React from "react";
import {Box, Button, Tooltip, Typography} from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {useTranslation} from "react-i18next";

/**
 * ErrorBoundary — capture les erreurs de rendu React dans l'arbre enfant.
 *
 * Usage global :
 *   <ErrorBoundary>
 *     <App />
 *   </ErrorBoundary>
 *
 * Usage local (zone lourde) :
 *   <ErrorBoundary fallback={<p>Ce composant a planté.</p>}>
 *     <ComponentLourd />
 *   </ErrorBoundary>
 */
class ErrorBoundaryClass extends React.Component {
    constructor(props) {
        super(props);
        this.state = {hasError: false, error: null, componentStack: null};
    }

    static getDerivedStateFromError(error) {
        return {hasError: true, error};
    }

    componentDidCatch(error, info) {
        this.setState({componentStack: info.componentStack});
        if (process.env.NODE_ENV !== "production") {
            console.error("[ErrorBoundary] Erreur capturée :", error, info.componentStack);
        }
    }

    handleReset = () => {
        this.setState({hasError: false, error: null, componentStack: null});
    };

    handleCopy = () => {
        const {error, componentStack} = this.state;
        const text = [
            error?.toString() ?? "",
            componentStack ?? "",
        ].join("\n\n");
        navigator.clipboard?.writeText(text).catch(() => {});
    };

    render() {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback;
            }
            return (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 2,
                        p: 4,
                        minHeight: this.props.minHeight ?? 200,
                    }}
                >
                    <ErrorOutlineIcon color="error" sx={{fontSize: 48}}/>
                    <Typography variant="h6" color="error">
                        {this.props.title ?? this.props.errorTitle}
                    </Typography>
                    {process.env.NODE_ENV !== "production" && this.state.error && (
                        <Typography
                            variant="caption"
                            sx={{
                                color: "text.secondary",
                                maxWidth: 600,
                                textAlign: "center"
                            }}>
                            {this.state.error.toString()}
                        </Typography>
                    )}
                    <Box sx={{display: "flex", gap: 1}}>
                        <Button variant="outlined" onClick={this.handleReset}>
                            {this.props.retryLabel ?? "Réessayer"}
                        </Button>
                        {process.env.NODE_ENV !== "production" && (
                            <Tooltip title={this.props.copyLabel ?? "Copier la stack trace"}>
                                <Button
                                    variant="outlined"
                                    color="inherit"
                                    startIcon={<ContentCopyIcon/>}
                                    onClick={this.handleCopy}
                                >
                                    {this.props.copyLabel ?? "Copier"}
                                </Button>
                            </Tooltip>
                        )}
                    </Box>
                </Box>
            );
        }
        return this.props.children;
    }
}

/** Wrapper fonctionnel pour injecter la traduction dans le class component */
export default function ErrorBoundary(props) {
    const {t} = useTranslation();
    return <ErrorBoundaryClass {...props} retryLabel={t("error.retry")} errorTitle={t("error.unexpected")}/>;
}

